export interface PrintBookBulkCsvRow {
  rowNumber: number
  orderIndex: number
  volumeLabel: string
  rawTitle: string
  title: string
  moduleIds: number[]
}

export interface PrintBookBulkCsvError {
  rowNumber: number
  code: 'INVALID_ORDER' | 'VOLUME_REQUIRED' | 'INVALID_MODULE_ID' | 'DUPLICATE_ORDER'
  message: string
}

export interface PrintBookBulkCsvParseResult {
  rows: PrintBookBulkCsvRow[]
  errors: PrintBookBulkCsvError[]
  hasErrors: boolean
}

interface ParseOptions {
  bookLabel: string
  titlePattern: string
}

const HEADER_NAMES = ['orderIndex', 'volumeLabel', 'title', 'moduleIds']

export function buildPrintBookTitle(pattern: string, bookLabel: string, volumeLabel: string) {
  const title = pattern.replace(/\{bookLabel\}/g, bookLabel).replace(/\{volumeLabel\}/g, volumeLabel).trim()
  return title.length > 0 ? title : `${bookLabel} ${volumeLabel}`.trim()
}

function hasHeader(cells: string[]) {
  return HEADER_NAMES.every((name, index) => cells[index]?.trim() === name)
}

export function parsePrintBookBulkCsv(input: string, options: ParseOptions): PrintBookBulkCsvParseResult {
  const physicalRows = input
    .trim()
    .split(/\r?\n/)
    .map((line, index) => ({ line, rowNumber: index + 1 }))
    .filter(row => row.line.trim().length > 0)

  const rows: PrintBookBulkCsvRow[] = []
  const errors: PrintBookBulkCsvError[] = []
  const dataRows = physicalRows[0] && hasHeader(physicalRows[0].line.split(',').map(cell => cell.trim()))
    ? physicalRows.slice(1)
    : physicalRows

  const rowNumbersByOrderIndex = new Map<number, number[]>()

  dataRows.forEach(({ line, rowNumber }) => {
    const [orderCell = '', volumeCell = '', titleCell = '', moduleCell = ''] = line.split(',').map(cell => cell.trim())
    const orderIndex = Number(orderCell)
    const orderValid = orderCell.length > 0 && Number.isInteger(orderIndex)
    if (orderValid) {
      rowNumbersByOrderIndex.set(orderIndex, [...(rowNumbersByOrderIndex.get(orderIndex) ?? []), rowNumber])
    }

    if (volumeCell.length === 0) {
      errors.push({ rowNumber, code: 'VOLUME_REQUIRED', message: 'volumeLabel is required' })
    }

    const moduleTokens = moduleCell.split('|').map(token => token.trim()).filter(Boolean)
    const moduleIds = moduleTokens.map(token => Number(token))
    const modulesValid = moduleIds.every(id => Number.isInteger(id))
    if (!modulesValid) {
      errors.push({ rowNumber, code: 'INVALID_MODULE_ID', message: 'moduleIds must contain integers separated by |' })
    }

    if (!orderValid) {
      errors.push({ rowNumber, code: 'INVALID_ORDER', message: 'orderIndex must be an integer' })
    }

    if (orderValid && volumeCell.length > 0 && modulesValid) {
      rows.push({
        rowNumber,
        orderIndex,
        volumeLabel: volumeCell,
        rawTitle: titleCell,
        title: titleCell.length > 0 ? titleCell : buildPrintBookTitle(options.titlePattern, options.bookLabel, volumeCell),
        moduleIds,
      })
    }
  })

  rowNumbersByOrderIndex.forEach(rowNumbers => {
    if (rowNumbers.length > 1) {
      errors.push({ rowNumber: rowNumbers[1], code: 'DUPLICATE_ORDER', message: 'orderIndex is duplicated' })
    }
  })

  return { rows, errors, hasErrors: errors.length > 0 }
}
